import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { type SortingState } from "@tanstack/react-table";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type {
  ColDef,
  ICellRendererParams,
  IHeaderParams,
  CellClickedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent
} from "ag-grid-community";
import { Checkbox, CheckboxSize } from "shared/components";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import {
  Lead,
  LeadType,
  NON_PUNITIVE_FRAUD_INDICATOR_TYPES
} from "views/leads-id/types";
import { cx } from "shared/utils/classnames";
import { LeadTypeBadge } from "shared/app/LeadsTableView/components/LeadTypeBadge";
import { ApprovalLikelihoodBadge } from "shared/app/LeadsTableView/components/ApprovalLikelihoodBadge";
import { PrescreeningResultBadge } from "shared/app/PrescreeningResult";
import { WaitlistLeadIndicator } from "shared/app/LeadsTableView/components/WaitlistLeadIndicator";
import { useLayout } from "providers/Layout/hooks";
import { formatDate } from "views/applications-id-report/utils";
import { LastNoteCell } from "shared/app/Notes/components/LastNoteCell";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { formatPhone } from "shared/utils";
import { CantTextBadge } from "shared/app/CantTextBadge";
import { findUndeliverable } from "shared/utils/sms-undeliverable";
import { useUndeliverableNumbers } from "views/inbox/api/use-undeliverable-numbers";
import type { UndeliverableNumber } from "views/inbox/types";
import { FOLLOW_UP_SHORT_LABEL, FollowUpPill } from "shared/app/CallFollowUp";
import { LEAD_ACTIVITY_TYPE_LABELS } from "shared/app/LeadsTableView/slices/LeadsTable/constants";
import { Stage } from "views/settings-crm/types/stage";
import {
  isLeadSelected,
  type LeadSelection
} from "shared/app/LeadsTableView/hooks/useLeadSelection";
import { toStageOptions } from "shared/app/LeadsTableView/utils/stageOptions";
import { StageSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/StageSelect";
import { AssigneeSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/AssigneeSelect";
import { StarCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/StarCell";
import { MessageCell } from "shared/app/LeadsTableView/slices/LeadsTable/components/MessageCell";
import { LeadTagsPicker } from "shared/app/LeadTags/components/LeadTagsPicker";
import { HeaderWithSort } from "shared/app/TaskTableView/slices/TaskTable/HeaderWithSort";
import { formatTaskCreatedDate } from "shared/app/TaskTableView/constants";
import { ColumnVisibilityItem, useAuthContext } from "providers/Auth";
import { LeadColumnId } from "shared/app/LeadsTableView/constants";
import {
  getLeadColumnHeader,
  getVisibleLeadColumnIds
} from "shared/app/LeadsTableView/utils/leadColumns";

const CHECKBOX_COL_ID = "checkbox";
const STAR_COL_ID = "star";
const MESSAGE_COL_ID = "message";
// Cells with their own controls: clicking them must not open the Lead.
const INTERACTIVE_COL_IDS = new Set<string>([
  CHECKBOX_COL_ID,
  STAR_COL_ID,
  MESSAGE_COL_ID,
  LeadColumnId.Tags,
  LeadColumnId.AssignedTo,
  LeadColumnId.StageSelector
]);

// Stable row ids let the grid update a row in place when its tags change, so an
// open tag picker stays mounted instead of the row being rebuilt.
const getLeadRowId = (params: GetRowIdParams<Lead>) => String(params.data.id);
// Stable empty default so an omitted ownerStagesData doesn't hand a fresh [] to
// the ownerStageOptions memo every render.
const EMPTY_STAGES: Stage[] = [];

// Selection flows through ag-grid `context` (not through the column defs) so a click
// re-renders only the checkbox column via refreshCells/refreshHeader instead of
// rebuilding every column definition.
interface LeadsGridContext {
  selection: LeadSelection;
  pageLeadIds: number[];
  onToggleRow: (id: number) => void;
  onTogglePage: (ids: number[], checked: boolean) => void;
  undeliverableNumbers: Map<string, UndeliverableNumber>;
}

const SelectAllHeader = (params: IHeaderParams<Lead>) => {
  const ctx = params.context as LeadsGridContext;
  const selectedOnPage = ctx.pageLeadIds.filter((id) =>
    isLeadSelected(ctx.selection, id)
  ).length;
  const allSelected =
    ctx.pageLeadIds.length > 0 && selectedOnPage === ctx.pageLeadIds.length;
  return (
    <div
      className="flex items-center justify-center w-full h-full"
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        wrapperClassName="!w-auto !flex-none !p-3 justify-center"
        size={CheckboxSize.SM}
        checked={allSelected}
        indeterminate={selectedOnPage > 0 && !allSelected}
        disabled={ctx.pageLeadIds.length === 0}
        onChange={() => ctx.onTogglePage(ctx.pageLeadIds, !allSelected)}
      />
    </div>
  );
};

const SelectRowCell = (params: ICellRendererParams<Lead>) => {
  const row = params.data;
  if (!row) return null;
  const ctx = params.context as LeadsGridContext;
  return (
    <div
      className="flex items-center justify-center w-full h-full"
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        wrapperClassName="!w-auto !flex-none !p-3 justify-center"
        size={CheckboxSize.SM}
        checked={isLeadSelected(ctx.selection, row.id)}
        onChange={() => ctx.onToggleRow(row.id)}
      />
    </div>
  );
};

export const LeadsTable = ({
  data,
  stagesData,
  ownerStagesData = EMPTY_STAGES,
  isLoading,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  sorting,
  onSortingChange,
  columnsVisibility,
  isLeadTypeEnabled = false,
  isLeadScoringEnabled = false,
  hidePropertyInterested = false,
  selection,
  onToggleRow,
  onTogglePage,
  enableSelectAll = false,
  noRowsFallback
}: {
  data?: Lead[];
  stagesData: Stage[];
  ownerStagesData?: Stage[];
  isLoading?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  columnsVisibility: ColumnVisibilityItem[];
  isLeadTypeEnabled?: boolean;
  isLeadScoringEnabled?: boolean;
  hidePropertyInterested?: boolean;
  selection: LeadSelection;
  onToggleRow: (id: number) => void;
  onTogglePage: (ids: number[], checked: boolean) => void;
  // Header "select this page" checkbox. Only the standalone /leads page turns it on
  // (CRM-1449 decision 7); embedded contact/property/unit tables stay per-row.
  enableSelectAll?: boolean;
  noRowsFallback?: ReactNode;
}) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();
  const showsNeedsFollowUp = Boolean(type.isCrmAiVoiceAgent);
  // The dictionary is a fresh object every render; depending on its strings keeps
  // the column defs (and so every mounted cell, e.g. an open tag picker) stable.
  const { Property: propertyLabel, Unit: unitLabel } =
    useUnitsPropertiesDictionary();

  const handleSort = (columnKey: string) => {
    const existingSort = sorting.find((s) => s.id === columnKey);

    let newSorting: SortingState;

    if (existingSort)
      newSorting = sorting.map((s) =>
        s.id === columnKey ? { ...s, desc: !s.desc } : s
      );
    else newSorting = [{ id: columnKey, desc: true }];

    onSortingChange(newSorting);
  };

  const getSortDirection = (columnKey: string) => {
    const sortItem = sorting.find((s) => s.id === columnKey);
    if (!sortItem) return undefined;
    return sortItem.desc ? "DESC" : ("ASC" as const);
  };

  const stageOptions = useMemo(() => toStageOptions(stagesData), [stagesData]);

  const ownerStageOptions = useMemo(
    () => toStageOptions(ownerStagesData),
    [ownerStagesData]
  );

  const columns = useMemo<ColDef<Lead>[]>(() => {
    const headerLabels = { propertyLabel, unitLabel };
    const checkboxCol: ColDef<Lead> = {
      colId: CHECKBOX_COL_ID,
      headerName: "",
      headerComponent: enableSelectAll ? SelectAllHeader : undefined,
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: SelectRowCell
    };

    const starCol: ColDef<Lead> = {
      colId: STAR_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return <StarCell leadId={row.id} starred={row.starred} />;
      }
    };

    const messageCol: ColDef<Lead> = {
      colId: MESSAGE_COL_ID,
      headerName: "",
      width: 52,
      minWidth: 52,
      pinned: "left",
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<Lead>) => {
        const row = params.data;
        if (!row) return null;
        return <MessageCell lead={row} />;
      }
    };

    const colDefById: Record<LeadColumnId, ColDef<Lead>> = {
      [LeadColumnId.Name]: {
        colId: LeadColumnId.Name,
        headerName: getLeadColumnHeader(LeadColumnId.Name, headerLabels),
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const ctx = params.context as LeadsGridContext;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-medium text-sm truncate">
                {row.first_name} {row.last_name}
              </p>
              {row.primary_phone ? (
                <div className="flex min-w-0 items-center gap-1">
                  <p className="text-content-400 text-xs font-normal truncate">
                    {formatPhone(row.primary_phone)}
                  </p>
                  <CantTextBadge
                    status={findUndeliverable(
                      row.primary_phone,
                      ctx.undeliverableNumbers
                    )}
                  />
                </div>
              ) : (
                <p className="text-content-400 text-xs font-normal truncate">
                  {row.primary_email}
                </p>
              )}
            </div>
          );
        }
      },
      [LeadColumnId.NeedsFollowUp]: {
        colId: LeadColumnId.NeedsFollowUp,
        headerName: "Needs follow-up",
        minWidth: 170,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const requestType = params.data?.open_follow_up_request_type;
          if (!requestType)
            return <p className="text-content-400 text-sm">—</p>;
          return <FollowUpPill label={FOLLOW_UP_SHORT_LABEL[requestType]} />;
        }
      },
      [LeadColumnId.PropertyInterested]: {
        colId: LeadColumnId.PropertyInterested,
        headerName: getLeadColumnHeader(
          LeadColumnId.PropertyInterested,
          headerLabels
        ),
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col gap-1">
              <p className="text-content-1000 font-normal text-sm truncate">
                {row.property?.name || "-"}
              </p>
              <p className="text-content-400 text-xs font-normal truncate">
                {row.property?.address.one_line_address || "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.UnitInterested]: {
        colId: LeadColumnId.UnitInterested,
        headerName: getLeadColumnHeader(
          LeadColumnId.UnitInterested,
          headerLabels
        ),
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex items-center gap-1.5 min-w-0">
              {row.from_waitlist ? (
                <WaitlistLeadIndicator
                  unitTitle={row.unit?.title}
                  joinedAt={row.created_at}
                />
              ) : null}
              <p className="text-content-1000 font-normal text-sm truncate">
                {row.unit?.title || "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.Type]: {
        colId: LeadColumnId.Type,
        headerName: getLeadColumnHeader(LeadColumnId.Type, headerLabels),
        minWidth: 120,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          if (!row.lead_type)
            return <span className="text-content-1000 text-sm">-</span>;
          return <LeadTypeBadge leadType={row.lead_type} />;
        }
      },
      [LeadColumnId.Source]: {
        colId: LeadColumnId.Source,
        headerName: getLeadColumnHeader(LeadColumnId.Source, headerLabels),
        minWidth: 200,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-1000 font-medium text-sm truncate">
              {row.source?.name || "-"}
            </p>
          );
        }
      },
      [LeadColumnId.Tags]: {
        colId: LeadColumnId.Tags,
        headerName: getLeadColumnHeader(LeadColumnId.Tags, headerLabels),
        minWidth: 260,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            // ag-theme-boom clips every direct child of .ag-cell, and .ag-cell clips at
            // its 1px transparent border (padding = --ag-cell-horizontal-padding - 1px).
            // The wrapper spans that padding and stops at the border, so the trigger's
            // hover box shows whole and its tags still line up with the header text.
            <div className="flex items-center h-full -mx-[calc(var(--ag-cell-horizontal-padding)_-_1px)] w-[calc(100%_+_2_*_(var(--ag-cell-horizontal-padding)_-_1px))]">
              <LeadTagsPicker
                leadId={row.id}
                tags={row.tags}
                emptyText="-"
                className="min-h-[40px]"
              />
            </div>
          );
        }
      },
      [LeadColumnId.Score]: {
        colId: LeadColumnId.Score,
        headerName: getLeadColumnHeader(LeadColumnId.Score, headerLabels),
        minWidth: 140,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label={getLeadColumnHeader(LeadColumnId.Score, headerLabels)}
            columnKey={LeadColumnId.Score}
            sortDirection={getSortDirection(LeadColumnId.Score)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          if (
            !row.approval_likelihood_band &&
            !row.approval_likelihood_insufficient_data
          )
            return <span className="text-content-1000 text-sm">-</span>;
          return (
            <ApprovalLikelihoodBadge
              band={row.approval_likelihood_band}
              insufficientData={row.approval_likelihood_insufficient_data}
            />
          );
        }
      },
      [LeadColumnId.PrescreeningResult]: {
        colId: LeadColumnId.PrescreeningResult,
        headerName: getLeadColumnHeader(
          LeadColumnId.PrescreeningResult,
          headerLabels
        ),
        minWidth: 180,
        resizable: true,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          if (
            row.lead_type === LeadType.Owner ||
            row.lead_type === LeadType.Realtor
          )
            return <span className="text-content-1000 text-sm">-</span>;
          return (
            <PrescreeningResultBadge
              result={row.prescreening_result}
              reason={row.prescreening_result_reason}
            />
          );
        }
      },
      [LeadColumnId.FraudIndicators]: {
        colId: LeadColumnId.FraudIndicators,
        headerName: getLeadColumnHeader(
          LeadColumnId.FraudIndicators,
          headerLabels
        ),
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const count = row.fraud_indicator_count || 0;
          if (count === 0) return <p className="text-content-400 text-sm">-</p>;

          const indicators = row.fraud_indicators || [];
          const hasPunitiveIndicator =
            indicators.length === 0 ||
            indicators.some(
              (indicator) =>
                !NON_PUNITIVE_FRAUD_INDICATOR_TYPES.includes(indicator.type)
            );

          return (
            <span
              className={cx(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                hasPunitiveIndicator
                  ? "bg-danger-50 text-danger-500"
                  : "bg-warning-50 text-warning-700"
              )}
            >
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={14}
                className="shrink-0"
              />
              {count} indicator{count === 1 ? "" : "s"}
            </span>
          );
        }
      },
      [LeadColumnId.LastNote]: {
        colId: LeadColumnId.LastNote,
        headerName: getLeadColumnHeader(LeadColumnId.LastNote, headerLabels),
        minWidth: 200,
        width: 300,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label={getLeadColumnHeader(LeadColumnId.LastNote, headerLabels)}
            columnKey={LeadColumnId.LastNote}
            sortDirection={getSortDirection(LeadColumnId.LastNote)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const note = row.last_note
            ? {
                id: "",
                author: {
                  id: 0,
                  name: row.last_note.actor_name,
                  email: "",
                  author_role: "" as never
                },
                author_role: "" as never,
                message: row.last_note.rich_text?.document_text ?? "",
                rich_text: row.last_note.rich_text ?? undefined,
                created_at: row.last_note.occurred_at,
                source_name: row.last_note.source_name
              }
            : null;
          return (
            <div className="w-full" onClick={(e) => e.stopPropagation()}>
              <LastNoteCell
                note={note}
                onViewNotes={() => navigate(`/leads/${row.id}?activity=notes`)}
              />
            </div>
          );
        }
      },
      [LeadColumnId.RecentActivity]: {
        colId: LeadColumnId.RecentActivity,
        headerName: getLeadColumnHeader(
          LeadColumnId.RecentActivity,
          headerLabels
        ),
        width: 270,
        minWidth: 220,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label={getLeadColumnHeader(
              LeadColumnId.RecentActivity,
              headerLabels
            )}
            columnKey={LeadColumnId.RecentActivity}
            sortDirection={getSortDirection(LeadColumnId.RecentActivity)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const recentActivity = row.recent_activity_summary;
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm truncate">
                {recentActivity
                  ? LEAD_ACTIVITY_TYPE_LABELS[recentActivity.activity_type]
                  : "-"}
              </p>
              <p className="text-content-300 text-xs font-normal">
                {recentActivity
                  ? formatDate(recentActivity.occurred_at, true)
                  : "-"}
              </p>
            </div>
          );
        }
      },
      [LeadColumnId.CreatedAt]: {
        colId: LeadColumnId.CreatedAt,
        headerName: getLeadColumnHeader(LeadColumnId.CreatedAt, headerLabels),
        width: 180,
        minWidth: 140,
        resizable: true,
        sortable: false,
        headerComponent: () => (
          <HeaderWithSort
            label={getLeadColumnHeader(LeadColumnId.CreatedAt, headerLabels)}
            columnKey={LeadColumnId.CreatedAt}
            sortDirection={getSortDirection(LeadColumnId.CreatedAt)}
            onSort={handleSort}
          />
        ),
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          const { label, time } = formatTaskCreatedDate(row.created_at);
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm">{label}</p>
              <p className="text-content-300 text-xs">{time}</p>
            </div>
          );
        }
      },
      [LeadColumnId.AssignedTo]: {
        colId: LeadColumnId.AssignedTo,
        headerName: getLeadColumnHeader(LeadColumnId.AssignedTo, headerLabels),
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <AssigneeSelect
              value={row.assignees?.[0]?.id ?? null}
              rowId={String(row.id)}
            />
          );
        }
      },
      [LeadColumnId.StageSelector]: {
        colId: LeadColumnId.StageSelector,
        headerName: getLeadColumnHeader(
          LeadColumnId.StageSelector,
          headerLabels
        ),
        minWidth: 240,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Lead>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <StageSelect
              inputClassName="w-[210px]"
              value={row.current_stage?.id}
              options={
                row.lead_type === LeadType.Owner
                  ? ownerStageOptions
                  : stageOptions
              }
              rowId={String(row.id)}
            />
          );
        }
      }
    };

    const orderedDynamicCols = getVisibleLeadColumnIds({
      columnsVisibility,
      isLeadTypeEnabled,
      isLeadScoringEnabled,
      hidePropertyInterested,
      showsNeedsFollowUp
    }).map((colId) => colDefById[colId]);

    return [checkboxCol, starCol, messageCol, ...orderedDynamicCols];
  }, [
    enableSelectAll,
    stageOptions,
    ownerStageOptions,
    isLeadTypeEnabled,
    isLeadScoringEnabled,
    showsNeedsFollowUp,
    hidePropertyInterested,
    propertyLabel,
    unitLabel,
    sorting,
    columnsVisibility,
    navigate
  ]);

  const rows = useMemo<Lead[]>(() => data || [], [data]);

  const { sectionHeight } = useLayout();

  const pageLeadIds = useMemo(() => rows.map((lead) => lead.id), [rows]);

  const pagePhones = useMemo(
    () => rows.map((lead) => lead.primary_phone),
    [rows]
  );
  const undeliverableNumbers = useUndeliverableNumbers(pagePhones);

  const gridContext = useMemo<LeadsGridContext>(
    () => ({
      selection,
      pageLeadIds,
      onToggleRow,
      onTogglePage,
      undeliverableNumbers
    }),
    [selection, pageLeadIds, onToggleRow, onTogglePage, undeliverableNumbers]
  );

  const gridApiRef = useRef<GridApi | null>(null);
  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  // ag-grid does not re-render header/cell renderers when `context` changes on its own.
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    api.refreshHeader();
    api.refreshCells({ columns: [CHECKBOX_COL_ID], force: true });
  }, [gridContext]);

  useEffect(() => {
    gridApiRef.current?.refreshCells({
      columns: [LeadColumnId.Name],
      force: true
    });
  }, [undeliverableNumbers]);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<Lead>) => {
      const row = event.data;
      if (!row) return;
      if (INTERACTIVE_COL_IDS.has(event.column.getColId())) return;
      navigate(`/leads/${row.id}`);
    },
    [navigate]
  );

  return (
    <div className="relative">
      <AgGrid<Lead>
        viewPortHeight={sectionHeight}
        viewPortHeightOffset={
          viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
        }
        columns={columns}
        rows={rows}
        loading={isLoading}
        onCellClicked={onCellClicked}
        onGridReady={onGridReady}
        getRowId={getLeadRowId}
        noRowsFallback={noRowsFallback}
        context={gridContext}
        footer={pagination}
        showFooter={Boolean(pagination)}
        perPageRows={perPageRows}
        rowHeight={72}
        headerHeight={44}
      />
    </div>
  );
};
